// src/composables/useNumberToText.js
const UNIDADES = {
    0: 'CERO', 1: 'UN', 2: 'DOS', 3: 'TRES', 4: 'CUATRO',
    5: 'CINCO', 6: 'SEIS', 7: 'SIETE', 8: 'OCHO', 9: 'NUEVE',
    10: 'DIEZ', 11: 'ONCE', 12: 'DOCE', 13: 'TRECE', 14: 'CATORCE',
    15: 'QUINCE', 16: 'DIECISEIS', 17: 'DIECISIETE', 18: 'DIECIOCHO', 19: 'DIECINUEVE'
};

const DECENAS = {
    20: 'VEINTE', 30: 'TREINTA', 40: 'CUARENTA',
    50: 'CINCUENTA', 60: 'SESENTA', 70: 'SETENTA', 80: 'OCHENTA', 90: 'NOVENTA'
};

const CENTENAS = {
    100: 'CIENTO',
    200: 'DOSCIENTOS', 300: 'TRESCIENTOS',
    400: 'CUATROCIENTOS', 500: 'QUINIENTOS', 600: 'SEISCIENTOS',
    700: 'SETECIENTOS', 800: 'OCHOCIENTOS', 900: 'NOVECIENTOS'
};

/**
 * Función recursiva privada para convertir números enteros (< 1,000,000) a texto.
 */
function convertNumber(num) {
    num = parseInt(num, 10);
    let texto = '';

    if (num === 0) {
        return '';
    } else if (num < 20) {
        texto = UNIDADES[num];
    } else if (num < 100) {
        const d = Math.floor(num / 10) * 10;
        const u = num % 10;
        texto = DECENAS[d];
        if (u !== 0) {
            // Se usa 'Y' después de 30
            texto += ' Y ' + UNIDADES[u];
        }
    } else if (num < 1000) {
        const c = Math.floor(num / 100);
        const r = num % 100;

        if (c === 1) {
            // Caso especial: 100 es 'CIEN', 101-199 es 'CIENTO...'
            texto = (r === 0) ? 'CIEN' : 'CIENTO';
        } else {
            texto = CENTENAS[c * 100];
        }

        if (r !== 0) {
            texto += ' ' + convertNumber(r);
        }
    } else if (num < 1000000) {
        const m = Math.floor(num / 1000);
        const r = num % 1000;

        texto = (m === 1 ? 'MIL' : convertNumber(m) + ' MIL');

        if (r !== 0) {
            // Se agrega el resto después de "MIL"
            texto += ' ' + convertNumber(r);
        }
    }

    return texto.trim();
}

/**
 * Función principal para convertir una cantidad monetaria a texto.
 * Replica la función numeralToText de PHP.
 * @param {number|string} number - La cantidad a convertir (e.g., 1500.25).
 * @param {string} currency - Nombre de la moneda (e.g., 'DÓLAR', 'EURO').
 * @returns {string} La cantidad en texto con el formato monetario.
 */
export function numberToText(number, currency = 'DÓLAR') {
    const rawNum = parseFloat(number);

    if (isNaN(rawNum)) {
        return '';
    }

    // Asegurar 2 decimales y separar partes
    const numString = rawNum.toFixed(2);
    const parts = numString.split('.');

    let entero = parseInt(parts[0], 10);
    const decimal = parts.length > 1 ? parts[1] : '00';

    // ------------------------------------
    // 1. Parte entera (MONEDA)
    // ------------------------------------
    let textoEntero = convertNumber(entero);
    let currencyPlural = currency + 'ES'; // DÓLARES
    let currencySingular = currency;      // DÓLAR

    // Ajuste de singular/plural
    if (entero === 1) {
        currencyPlural = currencySingular;
    }

    // Si la parte entera es cero
    if (entero === 0) {
        textoEntero = 'CERO';
        currencyPlural = currencySingular + 'ES'; // 'CERO DÓLARES'
    }

    // ------------------------------------
    // 2. Resultado Final
    // ------------------------------------
    const textoDecimal = decimal.padStart(2, '0');
    const complementoMoneda = 'CENTAVOS DE ' + currency;

    // Formato: MONTO_LETRAS MONEDA CON XX/100 DE DÓLAR
    const resultado = `${textoEntero} ${currencyPlural} CON ${textoDecimal}/100 ${complementoMoneda}`;

    // Limpieza final de espacios extra
    return resultado.replace(/\s+/g, ' ').toUpperCase().trim();
}


/**
 * Composable de Vue para exponer la función.
 * @returns {{ numberToText: (number: number, currency?: string) => string }}
 */
export function useNumberToText() {
    // Si bien no hay estado reactivo, exportamos la función dentro de un composable
    // para mantener el patrón de diseño de Vue 3.
    return {
        numberToText
    };
}